import { isUndefined } from 'lodash';
import axios from 'src/utils/axios';
import parse from 'src/utils/parse';

class OrdersApi {
  async getOrders(
    search: string,
    page: number,
    pageSize: number,
    organizationId: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .get(
          `${
            process.env.REACT_APP_PIM
          }/orders?page=${page}&pageSize=${pageSize}${
            search ? `&search=${search}` : ''
          }&organizationId=${organizationId}`
        )
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch(() => {
          reject(new Error('internal Server Error'));
        });
    });
  }

  async getOrderById(orderId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .get(`${process.env.REACT_APP_PIM}/orders/${orderId}`)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getFirstOrder(): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .get(`${process.env.REACT_APP_PIM}/orders/firstorder`)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // Create order with fromOrg and toOrg
  async createOrder(payload: {
    orderNumber: string;
    fromOrg: string;
    toOrg: string;
    orderDate: string;
    vat: number;
    note?: string;
    products: Array<{
      productId: string;
      quantity: number;
      priceType: 'monthlyMsrp' | 'oneYearMsrp' | 'threeYearMsrp';
    }>;
  }): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${process.env.REACT_APP_PIM}/orders`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // Update existing order
  async updateOrder(
    orderId: string,
    payload: {
      orderNumber: string;
      fromOrg: string;
      toOrg: string;
      orderDate: string;
      vat: number;
      note?: string;
      products: Array<{
        productId: string;
        quantity: number;
        priceType: 'monthlyMsrp' | 'oneYearMsrp' | 'threeYearMsrp';
      }>;
    }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .patch(`${process.env.REACT_APP_PIM}/orders/${orderId}`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getProductsWithPriceBookPrices(
    organizationId: string,
    page: number = 0,
    pageSize: number = 20,
    search: string = '',
    isActive: boolean = true
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      axios
        .get(
          `${process.env.REACT_APP_PIM}/product/productsWithPriceBookPrices?page=${page}&pageSize=${pageSize}${searchParam}&isActive=${isActive}&organizationId=${organizationId}`
        )
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getProductPriceAgainstFromAndToOrg(
    fromOrganizationId: string,
    toOrganizationId: string,
    productId: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .get(
          `${process.env.REACT_APP_PIM}/product/productPriceAgainstFromAndToOrg`,
          {
            params: {
              fromOrganizationId,
              toOrganizationId,
              productId,
            },
          }
        )
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getResellerOrders(
    fromOrgId: string,
    toOrgId?: string,
    options?: { page?: number; pageSize?: number }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      // Convert array to comma-separated string if needed
      const resolvedToOrgId = toOrgId || fromOrgId;
      const queryParams = new URLSearchParams();
      queryParams.append('fromOrg', fromOrgId);
      queryParams.append('toOrg', resolvedToOrgId);
      if (options?.page != null) {
        queryParams.append('page', String(options.page));
      }
      if (options?.pageSize != null) {
        queryParams.append('pageSize', String(options.pageSize));
      }

      axios
        .get(
          `${
            process.env.REACT_APP_PIM
          }/orders/reseller?${queryParams.toString()}`
        )
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getQuotations(
    page: number,
    pageSize: number,
    search: string = '',
    fromOrg: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      axios
        .get(
          `${process.env.REACT_APP_PIM}/quotes?page=${page}&pageSize=${pageSize}${searchParam}&fromOrg=${fromOrg}`
        )
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async createQuotation(payload: {
    quotationNumber: string;
    fromOrg: string;
    toOrg: string;
    vat: number;
    discount: number;
    note: string;
    products: Array<{
      productId: string;
      quantity: number;
      unitPrice: number;
      priceType: string;
    }>;
  }): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${process.env.REACT_APP_PIM}/quotes`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async updateQuotation(
    quotationId: string,
    payload: {
      quotationNumber: string;
      fromOrg: string;
      toOrg: string;
      vat: number;
      discount: number;
      note: string;
      products: Array<{
        productId: string;
        quantity: number;
        unitPrice: number;
        priceType: string;
      }>;
    }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .patch(`${process.env.REACT_APP_PIM}/quotes/${quotationId}`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getInvoices(organizationId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .get(
          `${process.env.REACT_APP_PIM}/invoices/reseller?fromOrg=${organizationId}`
        )
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async downloadQuotationPDF(quoteId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      axios
        .get(`${process.env.REACT_APP_PIM}/quotes/${quoteId}/quotation-pdf`, {
          responseType: 'blob',
        })
        .then(async (response) => {
          // Check HTTP status code first
          if (response.status >= 200 && response.status < 300) {
            // Success response - return the PDF blob
            if (response.data) {
              resolve(response.data);
            } else {
              reject(new Error('Failed to download PDF'));
            }
          } else {
            // Non-success status code - treat as error
            try {
              const text = await response.data.text();
              reject(new Error(text || 'Failed to generate PDF'));
            } catch {
              reject(
                new Error(
                  'Failed to download PDF. The server returned an error.'
                )
              );
            }
          }
        })
        .catch(async (error) => {
          // Handle error responses
          if (error.response && error.response.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              // Try to parse as JSON first, fallback to plain text
              try {
                const errorData = JSON.parse(text);
                reject(
                  new Error(
                    errorData.message || text || 'Failed to generate PDF'
                  )
                );
              } catch {
                // Not JSON, use the text directly
                reject(new Error(text || 'Failed to download PDF'));
              }
            } catch {
              reject(new Error('Failed to download PDF. Please try again.'));
            }
          } else if (error.message) {
            reject(new Error(error.message));
          } else {
            reject(error);
          }
        });
    });
  }

  async downloadOrderPDF(orderId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      axios
        .get(`${process.env.REACT_APP_PIM}/orders/${orderId}/order-pdf`, {
          responseType: 'blob',
        })
        .then(async (response) => {
          // Check HTTP status code first
          if (response.status >= 200 && response.status < 300) {
            // Success response - return the PDF blob
            if (response.data) {
              resolve(response.data);
            } else {
              reject(new Error('Failed to download PDF'));
            }
          } else {
            // Non-success status code - treat as error
            try {
              const text = await response.data.text();
              reject(new Error(text || 'Failed to generate PDF'));
            } catch {
              reject(
                new Error(
                  'Failed to download PDF. The server returned an error.'
                )
              );
            }
          }
        })
        .catch(async (error) => {
          // Handle error responses
          if (error.response && error.response.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              // Try to parse as JSON first, fallback to plain text
              try {
                const errorData = JSON.parse(text);
                reject(
                  new Error(
                    errorData.message || text || 'Failed to generate PDF'
                  )
                );
              } catch {
                // Not JSON, use the text directly
                reject(new Error(text || 'Failed to download PDF'));
              }
            } catch {
              reject(new Error('Failed to download PDF. Please try again.'));
            }
          } else if (error.message) {
            reject(new Error(error.message));
          } else {
            reject(error);
          }
        });
    });
  }

  async downloadInvoicePDF(invoiceId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      axios
        .get(`${process.env.REACT_APP_PIM}/invoices/${invoiceId}/invoice-pdf`, {
          responseType: 'blob',
        })
        .then(async (response) => {
          // Check HTTP status code first
          if (response.status >= 200 && response.status < 300) {
            // Success response - return the PDF blob
            if (response.data) {
              resolve(response.data);
            } else {
              reject(new Error('Failed to download invoice PDF'));
            }
          } else {
            // Non-success status code - treat as error
            try {
              const text = await response.data.text();
              reject(new Error(text || 'Failed to generate invoice PDF'));
            } catch {
              reject(
                new Error(
                  'Failed to download invoice PDF. The server returned an error.'
                )
              );
            }
          }
        })
        .catch(async (error) => {
          // Handle error responses
          if (error.response && error.response.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              // Try to parse as JSON first, fallback to plain text
              try {
                const errorData = JSON.parse(text);
                reject(
                  new Error(
                    errorData.message ||
                      text ||
                      'Failed to generate invoice PDF'
                  )
                );
              } catch {
                // Not JSON, use the text directly
                reject(new Error(text || 'Failed to download invoice PDF'));
              }
            } catch {
              reject(
                new Error('Failed to download invoice PDF. Please try again.')
              );
            }
          } else if (error.message) {
            reject(new Error(error.message));
          } else {
            reject(error);
          }
        });
    });
  }
}

export const ordersApi = new OrdersApi();
